import {inject, injectable} from "inversify";
import {Player, Team} from "@/modules/team/entity";
import {PositionEnum} from "@/modules/base/enums";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {FormationDefinitionType} from "@/modules/team/types";
import {FormationEnum} from "@/modules/team/enums";

@injectable()
export default class LineupSkillCalculator {
  constructor(
    @inject(ConfigRepository) protected configRepository: ConfigRepository,
  ) {
  }

  public calculateLinedupSkillPlayerByTeam(team: Team, player: Player): number | null {
    if (player.lineup === null) {
      return null;
    }

    return this.calculateLinedupSkill(
      [player],
      this.configRepository.get('formations.all')[team.formation]
    );
  }

  public calculateLinedupSkillByTeam(team: Team, onlyPlayers?: Player[]): number {
    return this.calculateLinedupSkill(
      onlyPlayers || team.players,
      this.configRepository.get('formations.all')[team.formation]
    );
  }

  public calculateLinedupSkillByName(players: Player[], name: FormationEnum): number {
    return this.calculateLinedupSkill(
      players,
      this.configRepository.get('formations.all')[name]
    );
  }

  public calculateLinedupSkill(players: Player[], formationDef: FormationDefinitionType): number {
    return collect(players)
      .where('lineup', '!==', null)
      .sum(p => this.calculateSkill(p, formationDef)) as number;
  }

  public isMatching(player: Player, formationDef: FormationDefinitionType): boolean {
    if (!player.lineup) {
      throw 'Player is not lined up.';
    }

    const formationPosition = formationDef[player.lineup].position;

    return player.position === formationPosition;
  }

  public isSimilar(player: Player, formationDef: FormationDefinitionType): boolean {
    if (this.isMatching(player, formationDef)) {
      return false;
    }

    if (!player.lineup) {
      throw 'Player is not lined up.';
    }

    const formationPosition = formationDef[player.lineup].position;

    // @ts-ignore
    const similar: { [key: PositionEnum]: PositionEnum[] } = this.configRepository.get('formations.positions.similar');
    // @ts-ignore
    const similarPositions: PositionEnum[] = similar[formationPosition];

    // similar position
    return similarPositions.includes(player.position);
  }

  public isWrong(player: Player, formationDef: FormationDefinitionType): boolean {
    return !this.isMatching(player, formationDef) && !this.isSimilar(player, formationDef);
  }

  public calculateSkill(player: Player, formationDef: FormationDefinitionType): number {
    if (this.isMatching(player, formationDef)) {
      return Math.floor(this.getPlayersSkill(player));
    }

    if (this.isSimilar(player, formationDef)) {
      return Math.round(this.getPlayersSkill(player) * (this.configRepository.get('formations.positions.punishment.similar') as number));
    }

    return Math.round(this.getPlayersSkill(player) * (this.configRepository.get('formations.positions.punishment.wrong') as number));
  }

  public getPlayersSkill(player: Player) {
    return player.skill;
  }
}
